import { useCallback, useEffect, useState } from 'react';

import { NO_ACCESS_TYPES, PERMISSIONS } from '../../../../utils/constants';
import { SaveIcon, CogIcon, LockClosedIcon, AtSymbolIcon, UserIcon, DeviceMobileIcon } from '@heroicons/react/solid';

import {
    getAllUserGroups,
    changeDefaultUserGroup,
    editOwnProfile,
    changePassword,
    getOwnDetails
} from "../../../../utils/api";

import Button from '../../../SharedComponents/Button';
import Alert, { ALERT_TYPE } from '../../../Auth/Alert';
import DashboardContainer from '../../../SharedComponents/DashboardContainer';
import AccessControl from '../../../SharedComponents/AccessControl';
import Input from '../../../Auth/Input';
import useMountStatus from '../../../../hooks/useMountStatus';
import InputPlaceholder from "../../../SharedComponents/Loading/InputPlaceholder";
import SectionPlaceholder from "../../../SharedComponents/Loading/SectionPlaceholder";
import Toggle from "../../../SharedComponents/Form/Toggle";

function Settings() {
    const [alert, setAlert] = useState('');
    const [alertType, setAlertType] = useState(ALERT_TYPE.ERROR);
    const [userGroups, setUserGroups] = useState([]);
    const [defaultUserGroup, setDefaultUserGroup] = useState();
    const [currentPwd, setCurrentPwd] = useState();
    const [newPwd, setNewPwd] = useState();
    const [confirmNewPwd, setConfirmNewPwd] = useState();
    const [firstName, setFirstName] = useState();
    const [lastName, setLastName] = useState();
    const [emailAddress, setEmailAddress] = useState();
    const [mobileNumber, setMobileNumber] = useState();
    const [editOption, setEditOption] = useState('Default User Group');
    const [loading, setLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [changesMade, setChangesMade] = useState(false);
    const unmounted = useMountStatus();

    const handleSuccessfulEdit = async (message, editOption) => {
        setAlert(message);
        setAlertType(ALERT_TYPE.SUCCESS);
        await fetchData(editOption);
    };

    const handleChangeDefaultUserGroup = async (userGroupName) => {
        setSubmitLoading(true);
        try {
            await changeDefaultUserGroup(userGroupName);
            await handleSuccessfulEdit('You\'ve successfully changed the Default User Group to ' +
                userGroupName, 'Default User Group');
        } catch (err) {
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setSubmitLoading(false);
    };

    const handleResetPassword = async () => {
        if (newPwd !== confirmNewPwd) {
            setAlert("Please ensure the two passwords entered are identical");
            setAlertType(ALERT_TYPE.ERROR);
        }

        setSubmitLoading(true);
        try {
            await changePassword(newPwd, currentPwd);
            if (unmounted) return;
            setAlertType(ALERT_TYPE.SUCCESS);
            setAlert("Successfully reset password");
        } catch (err) {
            if (unmounted) return;
            setAlertType(ALERT_TYPE.ERROR);
            setAlert(err.message);
        }
        setSubmitLoading(false);
    };

    const handleUpdateProfile = async () => {
        setSubmitLoading(true);
        try {
            await editOwnProfile(emailAddress, firstName, lastName, mobileNumber);
            if (unmounted) return;
            setAlertType(ALERT_TYPE.SUCCESS);
            setAlert("Successfully updated profile");
        } catch (err) {
            if (unmounted) return;
            setAlertType(ALERT_TYPE.ERROR);
            setAlert(err.message);
        }
        setSubmitLoading(false);
    }

    const handleEditOptionClick = (editOption) => {
        editOption && setEditOption(editOption);
    };

    const fetchData = useCallback(async () => {
        setLoading(true);

        try {
            const userGroupsResponse = await getAllUserGroups();
            setDefaultUserGroup(userGroupsResponse.find(userGroup => userGroup.isDefault === true))

            if (unmounted) return;

            setUserGroups(userGroupsResponse);

            const userDetailsResponse = await getOwnDetails();

            if (unmounted) return;

            setEmailAddress(userDetailsResponse.emailAddress);
            setFirstName(userDetailsResponse.firstName);
            setLastName(userDetailsResponse.lastName);
            setMobileNumber(userDetailsResponse.mobileNumber);
        } catch (err) {
            if (unmounted) return;
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setLoading(false);
    }, [unmounted]);

    const handleOnClickSave = () => {
        switch (editOption) {
            case 'Default User Group':
                handleChangeDefaultUserGroup(defaultUserGroup);
                break;
            case 'Reset Password':
                handleResetPassword();
                break;
            case 'Profile':
                handleUpdateProfile();
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        fetchData();
    }, [editOption, fetchData]);

    const renderDefaultUserGroupScreen = () => (
        <AccessControl requiredPermission={PERMISSIONS.EDIT_DEFAULT_USER_GROUP} noAccessType={NO_ACCESS_TYPES.BLANK}>
            <div className="flex items-start justify-between pt-4 pb-2 px-4 rounded-t">
                <h3 className="text-md font-medium leading-6 text-indigo-900 capitalize ">
                    Change {editOption}
                </h3>
            </div>
            <div className="px-4">
                <p className="text-sm text-gray-600">
                    Your default User Group will be used for any new users you add to the system and is
                    specific for the organisation you are logged in with right now.
                </p>
            </div>
            <div className="relative px-4 mt-4 mb-4 flex w-full flex-wrap items-stretch">
                <label htmlFor={defaultUserGroup}
                       className="text-sm text-sm font-medium text-indigo-900 w-full">
                    Default User Group
                </label>

                {loading ?
                    <div className={"mt-2 w-1/4"}>
                        <InputPlaceholder/>
                    </div>
                    :
                    editOption === 'Default User Group' && userGroups &&
                    <select
                        id="defaultUserGroups"
                        name="defaultUserGroups"
                        disabled={loading}
                        defaultValue={loading ? "" : defaultUserGroup.name}
                        className={
                            'mt-2 bg-blue-gray-50 border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 ' +
                            'text-left focus:outline-none focus:ring-1 focus:ring-primary-600 ' +
                            'focus:border-primary-600 sm:text-sm'
                        }
                        onChange={(e) => {
                            setDefaultUserGroup(e.target.value);
                            setChangesMade(true);
                        }}
                    >
                        {loading &&
                            <option value="" className={'italic'}>
                                Loading User Groups..
                            </option>
                        }

                        {userGroups.map((userGroup, index) => (
                            <option
                                key={index}
                                value={userGroup.name}
                                className={'mt-1'}
                            >
                                {userGroup.name}
                            </option>
                        ))
                        }

                    </select>

                }
            </div>
        </AccessControl>
    );

    const renderResetPasswordScreen = () => (
        <AccessControl requiredPermission={PERMISSIONS.EDIT_OWN_PASSWORD} noAccessType={NO_ACCESS_TYPES.BLANK}>
            <div className="flex items-start justify-between pt-4 pb-2 px-4 rounded-t">
                <h3 className="text-md font-medium leading-6 text-indigo-900 capitalize ">
                    {editOption}
                </h3>
            </div>
            <div className="px-4">
                <p className="text-sm text-gray-600">
                    Reset your current password.
                </p>
            </div>
            <div className="relative px-4 mt-4 mb-4 flex flex-col w-full flex-wrap items-stretch">
                <div className={'mb-5'}>
                    <Input
                        type="password"
                        name="Current Password"
                        id="current_password"
                        placeholder="Current Password"
                        Icon={<LockClosedIcon className="h-4 w-4 text-gray-500"/>}
                        onChange={(textInput) => {
                            setCurrentPwd(textInput);
                            setChangesMade(true);
                        }}
                    />
                </div>
                <div className={'mb-5'}>
                    <Input
                        type="password"
                        name="New Password"
                        id="new_password"
                        placeholder="New Password"
                        Icon={<LockClosedIcon className="h-4 w-4 text-gray-500"/>}
                        onChange={(textInput) => {
                            setNewPwd(textInput);
                            setChangesMade(true);
                        }}
                    />
                </div>
                <div className={'mb-5'}>
                    <Input
                        type="password"
                        name="Confirm New Password"
                        id="confirm_current_password"
                        placeholder="Confirm New Password"
                        Icon={<LockClosedIcon className="h-4 w-4 text-gray-500"/>}
                        onChange={(textInput) => {
                            setConfirmNewPwd(textInput);
                            setChangesMade(true);
                        }}
                    />
                </div>
            </div>
        </AccessControl>
    );

    const renderUserProfileScreen = () => (
        <AccessControl requiredPermission={PERMISSIONS.EDIT_OWN_PASSWORD} noAccessType={NO_ACCESS_TYPES.BLANK}>
            <div className="flex items-start justify-between pt-4 pb-2 px-4 rounded-t">
                <h3 className="text-md font-medium leading-6 text-indigo-900 capitalize ">
                    {editOption}
                </h3>
            </div>
            <div className="px-4">
                <p className="text-sm text-gray-600">
                    Update your profile details.
                </p>
            </div>
            <div className="relative px-4 mt-4 mb-4 flex flex-col w-full flex-wrap items-stretch">
                {loading ?
                    <SectionPlaceholder/>
                    :
                    <>
                        <div className={'mb-5'}>
                            <Input
                                optional
                                type="text"
                                name="First name"
                                id="firstname"
                                placeholder={firstName}
                                onChange={(textInput) => {
                                    setFirstName(textInput);
                                    setChangesMade(true);
                                }}
                                Icon={<UserIcon className="h-4 w-4 text-gray-500"/>}
                            />
                        </div>
                        <div className={'mb-5'}>
                            <Input
                                optional
                                type="text"
                                name="Last name"
                                id="lastname"
                                placeholder={lastName}
                                onChange={(textInput) => {
                                    setLastName(textInput);
                                    setChangesMade(true);
                                }}
                                Icon={<UserIcon className="h-4 w-4 text-gray-500"/>}
                            />
                        </div>
                        <div className={'mb-5'}>
                            <Input
                                id="email"
                                type="email"
                                name="Email"
                                placeholder={emailAddress}
                                onChange={(textInput) => {
                                    setEmailAddress(textInput);
                                    setChangesMade(true);
                                }}
                                Icon={<AtSymbolIcon className="h-4 w-4 text-gray-500"/>}
                            />
                        </div>
                        <div className={'mb-5'}>
                            <Input
                                optional
                                id="mobile"
                                type="text"
                                name="Mobile "
                                placeholder={mobileNumber}
                                onChange={(textInput) => {
                                    setMobileNumber(textInput);
                                    setChangesMade(true);
                                }}
                                Icon={<DeviceMobileIcon className="h-4 w-4 text-gray-500"/>}
                            />
                        </div>
                    </>
                }
            </div>
        </AccessControl>
    );

    const renderSingleSignOn = () => (
        <AccessControl requiredPermission={PERMISSIONS.EDIT_OWN_PASSWORD} noAccessType={NO_ACCESS_TYPES.BLANK}>
            <div className="flex items-start justify-between pt-4 pb-2 px-4 rounded-t">
                <h3 className="text-md font-medium leading-6 text-indigo-900 capitalize ">
                    {editOption}
                </h3>
            </div>
            <div className="px-4">
                <p className="text-sm text-gray-600">
                    Enable or disable SSO
                </p>
            </div>
            <div className="relative px-4 mt-4 mb-4 flex flex-col w-full flex-wrap items-stretch">
                {loading ?
                    <SectionPlaceholder/>
                    :
                    <div className="flex flex-col gap-4">
                        <div className={
                            "text-sm w-full mx-auto rounded-md px-3 py-3 info flex flex-col gap-1 " +
                            "bg-amber-100 text-amber-700 align-center justify-center"
                        }>
                            <span className="font-medium">Please note:</span>
                            <div className="flex flex-col gap-1">
                                <div>1. All users need to be set up in your tenant (i.e Azure, Microsoft,
                                    Facebook)
                                </div>
                                <div>2. All users that are currently logged in will be logged out</div>
                                <div>3. It may take several minutes to finish the process, during this time users
                                    may have difficulty logging in
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-16">
                            <div className={"text-sm flex flex-col"}
                            >
                                <span className="font-medium text-indigo-900">Single Sign-On (SSO)</span>
                            </div>
                            <div className="flex space-between">
                                <Toggle name={"toggle-3"}/>
                            </div>
                        </div>
                    </div>
                }
            </div>
        </AccessControl>
    );

    return (
        <DashboardContainer title={'Settings'} titleIcon={<CogIcon className="block h-5 w-5 mr-2"/>}>
            <Alert
                text={alert}
                type={alertType}
                active={alert}
                close={() => setAlert('')}
            />
            <div className="grid grid-flow-row xl:grid-cols-8 lg:grid-cols-2 gap-6 min-h-0 overflow-x-scroll">
                <div className={"flex flex-row xl:flex-col gap-3 col-span-2 z-10 w-max xl:w-auto"}>
                    <AccessControl requiredPermission={PERMISSIONS.EDIT_OWN_USER_ACCOUNT}
                                   noAccessType={NO_ACCESS_TYPES.BLANK}>
                        <div className={
                            'p-3 text-sm rounded-md  flex items-center justify-between font-medium ' +
                            'shadow-sm hover:text-secondary-600 transition transition-all cursor-pointer ' +
                            (editOption === 'Profile' ? 'text-secondary-600 bg-secondary-50 ' :
                                'text-blue-gray-600 bg-white ') +
                            (loading && 'animate-pulse pointer-events-none')
                        }
                             onClick={() => handleEditOptionClick('Profile')}
                        >
                            <span>Profile</span>
                        </div>
                    </AccessControl>
                    <AccessControl requiredPermission={PERMISSIONS.EDIT_OWN_PASSWORD}
                                   noAccessType={NO_ACCESS_TYPES.BLANK}>
                        <div className={
                            'p-3 text-sm rounded-md  flex items-center justify-between font-medium ' +
                            'shadow-sm hover:text-secondary-600 transition transition-all cursor-pointer ' +
                            (editOption === 'Reset Password' ? 'text-secondary-600 bg-secondary-50 ' :
                                'text-blue-gray-600 bg-white ') +
                            (loading && 'animate-pulse pointer-events-none')
                        }
                             onClick={() => handleEditOptionClick('Reset Password')}
                        >
                            <span>Reset Password</span>
                        </div>
                    </AccessControl>
                    <AccessControl requiredPermission={PERMISSIONS.EDIT_DEFAULT_USER_GROUP}
                                   noAccessType={NO_ACCESS_TYPES.BLANK}>
                        <div className={
                            'p-3 text-sm rounded-md  flex items-center justify-between font-medium ' +
                            'shadow-sm hover:text-secondary-600 transition transition-all cursor-pointer ' +
                            (editOption === 'Default User Group' ? 'text-secondary-600 bg-secondary-50 ' :
                                'text-blue-gray-600 bg-white ') +
                            (loading && 'animate-pulse pointer-events-none')
                        }
                             onClick={() => handleEditOptionClick('Default User Group')}
                        >
                            <span>Default User Group</span>
                        </div>
                    </AccessControl>
                </div>
                <div className="col-span-6">
                    <div className={
                        'border-0 rounded-lg relative flex flex-col w-full bg-white outline-none ' +
                        'focus:outline-none shadow-sm'
                    }>
                        {editOption === 'Default User Group' && renderDefaultUserGroupScreen()}
                        {editOption === 'Reset Password' && renderResetPasswordScreen()}
                        {editOption === 'Profile' && renderUserProfileScreen()}
                        {editOption === 'Single Sign-On (SSO)' && renderSingleSignOn()}
                        <div className={
                            'p-4 flex items-center justify-end text-right rounded-md bg-gradient-to-l from-primary-50 to-secondary-50'
                        }>
                            <div className="flex flex-row gap-3">
                                <Button
                                    text="Save changes"
                                    icon={<SaveIcon className="block h-4 w-4 mr-2"/>}
                                    iconPositionLeft
                                    onClick={handleOnClickSave}
                                    loading={submitLoading}
                                    disabled={loading || !changesMade}
                                    extraStyling={
                                        "text-white focus:ring-inset focus:ring-1 bg-secondary-500 " +
                                        "hover:bg-secondary-600 focus:outline-none focus:ring-secondary-500"
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </DashboardContainer>
    );
}

export default Settings;
